import React, { useEffect, useRef, useState } from 'react'
import { Form, Input, Typography } from 'antd'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { Modal } from 'src/components/Ant'
import {
  useGetInitialValues,
  getValidationSchema,
  validate
} from 'src/core/forms/request/itinerary'
import useStandardResource from 'src/core/hooks/use-standard-resource'
import { useT } from 'src/core/hooks/use-translation'
import {
  saveRequestItinerary,
  resetSaveFullRequest
} from 'src/core/store/ducks/requests/actions'
import { Request, RequestItinerary } from 'src/types'
import { ApplicationState } from 'src/store'

import { WrapAlert } from './styles'
import FormFields from '../FormFields'

type Props = {
  itemEdit?: RequestItinerary
  request: Request
  toggle(): void
  visible: boolean
}

function ModalForm({ itemEdit, request, toggle, visible }: Props): JSX.Element {
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const dispatch = useDispatch()
  const [saving, setSaving] = useState<boolean>(false)
  const refActivityInput = useRef<Input>(null)
  const refFormik = useRef<Formik<RequestItinerary>>(null)

  const referenceStandard = useStandardResource('references')
  const initialValues = useGetInitialValues()
  const { t } = useT()

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        refFormik.current?.resetForm(
          !itemEdit
            ? {
                ...initialValues,
                reference_id: referenceStandard
                  ? referenceStandard.reference_id.toString()
                  : null
              }
            : { ...itemEdit, reference_id: itemEdit.reference_id.toString() }
        )

        if (refActivityInput.current) {
          refActivityInput.current.focus()
        }
      }, 100)
    }
  }, [initialValues, itemEdit, referenceStandard, visible])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        toggle()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  return (
    <Modal
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      forceRender
      maskClosable={!saving}
      okButtonProps={{ loading: saving }}
      onCancel={() => {
        toggle()
        dispatch(resetSaveFullRequest())
      }}
      onOk={() => refFormik.current.submitForm()}
      title={
        <>
          {itemEdit
            ? t('web.itinerary.form.title-edit')
            : t('web.itinerary.form.title')}
          {!!itemEdit && (
            <Typography.Text code>
              #{itemEdit.request_itinerary_id}
            </Typography.Text>
          )}
        </>
      }
      visible={visible}
      width={820}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          dispatch(saveRequestItinerary(values))
        }}
        ref={refFormik}
        render={formikProps => (
          <Form onSubmit={formikProps.handleSubmit}>
            <FormFields
              formikProps={formikProps}
              organization_id={request.organization_id}
              refActivityInput={refActivityInput}
            />
            {requests.saveFullRequestError && (
              <WrapAlert
                message={requests.saveFullRequestError}
                type="error"
                showIcon
              />
            )}
          </Form>
        )}
        validate={values => validate({ request, values })}
        validationSchema={getValidationSchema()}
      />
    </Modal>
  )
}

export default ModalForm
